import { readSkyLoopTiming, skyLoopReviewEnabled } from './243-sky-loop-timing.mjs';
const CAPACITY = 300;
const percentile = (values, length, fraction) => {
  if (!length) return null;
  const sorted = Array.from(values.subarray(0, length)).sort((a, b) => a - b);
  return sorted[Math.ceil(length * fraction) - 1];
};

/** Bounded active-frame samples in milliseconds. Cadence includes the browser's
 * scheduling and simulation; render CPU measures submission, never GPU time.
 * Loading, pause and hidden-tab intervals do not count as gameplay. Repeated
 * >250ms active stalls are counted separately rather than hidden as good FPS.
 * Call begin/end once per rendered frame, with an injected monotonic clock. */
export function createSkyFrameSamples() {
  const cadence = new Float32Array(CAPACITY),
    cpu = new Float32Array(CAPACITY);
  let count = 0,
    cursor = 0,
    prior = null,
    latestTime = null,
    started = null,
    key = null,
    stalls = 0;
  return {
    begin({ now, time, active, sceneKey }) {
      started = null;
      if (!active || !Number.isFinite(now) || !Number.isFinite(time)) {
        prior = null;
        return;
      }
      if (
        key !== sceneKey ||
        (latestTime !== null && time < latestTime) ||
        (prior && now < prior.now)
      ) {
        count = cursor = stalls = 0;
        prior = null;
        key = sceneKey;
      }
      latestTime = time;
      if (prior?.time === time) return;
      const delta = prior ? now - prior.now : null;
      if (delta > 250) stalls++;
      started = { now, delta: delta > 0 && delta <= 250 ? delta : null };
      prior = { now, time };
    },
    end(now) {
      if (!started) return;
      const elapsed = now - started.now;
      if (started.delta !== null && Number.isFinite(elapsed) && elapsed >= 0) {
        cadence[cursor] = started.delta;
        cpu[cursor] = elapsed;
        count = Math.min(count + 1, CAPACITY);
        cursor = (cursor + 1) % CAPACITY;
      }
      started = null;
    },
    diagnostics: () => ({
      frameSamples: count,
      frameMedianMs: percentile(cadence, count, 0.5),
      frameP95Ms: percentile(cadence, count, 0.95),
      renderCpuP95Ms: percentile(cpu, count, 0.95),
      activeStalls: stalls,
    }),
  };
}

/** Local opt-in review overlay. No storage, uploads, new RAF or GPU readback.
 * Quantiles and DOM text update at most once per second. The normal game has no
 * overlay; source contacts and adaptive decoration remain independently owned. */
export function createSkyRenderMonitor(canvas, renderer, adaptive, readDetails) {
  const samples = createSkyFrameSamples();
  const doc = canvas.ownerDocument;
  const enabled = skyLoopReviewEnabled(canvas);
  const output = enabled ? doc.createElement('output') : null;
  if (output) {
    output.className = 'sky-review-metrics';
    output.setAttribute('aria-live', 'off');
    output.setAttribute('aria-label', 'Local rendering review');
    canvas.parentElement?.append(output);
  }
  let lastPaint = -Infinity,
    label = '',
    active = false,
    lastActiveRender = null;
  const diagnostics = () => ({
    drawCalls: lastActiveRender?.calls ?? renderer.info.render.calls,
    triangles: lastActiveRender?.triangles ?? renderer.info.render.triangles,
    ...readDetails(),
    ...adaptive.diagnostics(),
    ...samples.diagnostics(),
    ...readSkyLoopTiming(canvas),
  });
  return {
    begin(state, cinematic, ready) {
      active =
        !cinematic && state.status === 'playing' && doc?.visibilityState !== 'hidden' && ready;
      label = `L${state.sector + 1} ${state.boss ? 'BOSS' : 'APPROACH'}`;
      samples.begin({ now: performance.now(), time: state.time, active, sceneKey: label });
    },
    end() {
      const now = performance.now();
      samples.end(now);
      if (active)
        lastActiveRender = {
          calls: renderer.info.render.calls,
          triangles: renderer.info.render.triangles,
        };
      if (!output || now - lastPaint < 1000) return;
      lastPaint = now;
      const d = diagnostics();
      const number = (v) => (Number.isFinite(v) ? v.toFixed(1) : '—');
      output.textContent =
        `REVIEW · ${label} · ${active ? d.decorationTier : 'HELD'}\n` +
        `${number(d.frameMedianMs ? 1000 / d.frameMedianMs : null)} FPS median · ` +
        `${number(d.frameP95Ms)} ms p95\n` +
        `${number(d.renderCpuP95Ms)} ms render CPU p95 · ${d.drawCalls} draws\n` +
        `${d.triangles.toLocaleString()} triangles · ${d.frameSamples} frames · ${d.activeStalls} stalls` +
        (d.loopSamples
          ? `\nLOOP p95 ms · input ${number(d.inputP95)} · sim ${number(d.simulationP95)}\n` +
            `prepare ${number(d.prepareP95)} · audio ${number(d.audioP95)} · paint CPU ${number(d.paintP95)}\n` +
            `${number(d.stepsP95)} steps/frame p95 · ${d.loopSamples} loop frames`
          : '');
    },
    diagnostics,
    dispose: () => output?.remove(),
  };
}
